/**
 * OrganizationContext — Phase 3.
 *
 * Purpose: a single lightweight cache of the user's org memberships and the
 * domains they can access. Mounted once near the App root so the sidebar,
 * topbar, and org pages can read it without re-fetching.
 *
 * NOT a workspace switcher. NOT a parallel auth system. NOT a permissions
 * provider. It only answers two questions:
 *
 *   1. Does the current user belong to any organizations?
 *      → If no, hide all org UI. Solo-artist UX is unchanged.
 *
 *   2. Which domain slugs can the current user act on?
 *      → owned domain + any domains linked via an active org membership.
 *
 * Failure mode: any backend error → empty arrays + `error` field set, never
 * throws. Solo-artist hot path stays cheap (one HTTP call on login, skipped
 * for unauthenticated users).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  listMyOrganizations,
  listAccessibleDomains,
  type AccessibleDomain,
  type OrganizationWithRole,
} from '../services/organizationApi';

interface OrganizationContextValue {
  loading: boolean;
  error: string | null;
  organizations: OrganizationWithRole[];
  accessibleDomains: AccessibleDomain[];
  /** True iff the user belongs to at least one organization. */
  hasOrganizations: boolean;
  /**
   * True iff the user holds an owner/admin role in at least one
   * management company. Scoped Members (legacy DB role `'manager'`) are
   * artist-managers — they have no business in the management-company
   * workspace and should not see its UI surfaces.
   */
  hasManagementRole: boolean;
  /** Refresh both memberships + accessible domains. */
  refresh: () => Promise<void>;
}

const EMPTY: OrganizationContextValue = {
  loading: false,
  error: null,
  organizations: [],
  accessibleDomains: [],
  hasOrganizations: false,
  hasManagementRole: false,
  refresh: async () => {},
};

const OrganizationContext = createContext<OrganizationContextValue>(EMPTY);

interface ProviderProps {
  children: React.ReactNode;
  /** Pass the auth token. When empty/null, the provider stays inert. */
  token: string | null;
}

export const OrganizationProvider: React.FC<ProviderProps> = ({ children, token }) => {
  const [organizations, setOrganizations] = useState<OrganizationWithRole[]>([]);
  const [accessibleDomains, setAccessibleDomains] = useState<AccessibleDomain[]>([]);
  // Initialize `loading` based on token presence so the very first render
  // after login does not briefly evaluate as "loaded with empty data" and
  // flash UI keyed on no-org / no-access state. The effect below kicks off
  // the real fetch immediately; we just want the in-between to read as
  // loading rather than empty.
  const [loading, setLoading] = useState<boolean>(!!token);
  const [error, setError] = useState<string | null>(null);

  // Phase 3.5 — stale-response guard. Each refresh() bumps this counter and
  // captures the new value; only the latest call may commit results. Prevents
  // a logout-mid-flight from being overwritten by a late successful response.
  const fetchIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const myFetchId = ++fetchIdRef.current;
    if (!token) {
      setOrganizations([]);
      setAccessibleDomains([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [orgs, doms] = await Promise.all([
        listMyOrganizations().catch(() => []),
        listAccessibleDomains().catch(() => []),
      ]);
      if (myFetchId !== fetchIdRef.current) return; // stale
      setOrganizations(orgs);
      setAccessibleDomains(doms);
    } catch (err: any) {
      if (myFetchId !== fetchIdRef.current) return;
      setError(err?.message || 'Failed to load organizations');
    } finally {
      if (myFetchId === fetchIdRef.current) setLoading(false);
    }
  }, [token]);

  // Fetch once whenever the token changes (login / logout). No refetch on
  // tenant switch — memberships are user-scoped, not tenant-scoped.
  useEffect(() => {
    if (token) {
      refresh();
    } else {
      // Bump the fetch id so any in-flight refresh from the prior token
      // cannot commit its results after logout.
      fetchIdRef.current++;
      setOrganizations([]);
      setAccessibleDomains([]);
      setError(null);
      setLoading(false);
    }
  }, [token, refresh]);

  const value = useMemo<OrganizationContextValue>(
    () => ({
      loading,
      error,
      organizations,
      accessibleDomains,
      hasOrganizations: organizations.length > 0,
      hasManagementRole: organizations.some(
        (o) => o.myRole === 'owner' || o.myRole === 'admin',
      ),
      refresh,
    }),
    [loading, error, organizations, accessibleDomains, refresh],
  );

  return (
    <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
  );
};

export function useOrganizations(): OrganizationContextValue {
  return useContext(OrganizationContext);
}
